import { Component, h, Prop, Element, Host } from '@stencil/core';
import { stencilEnv } from '../../global/_config';
import {
  CmsLinkConfig,
  Logger,
  PaginationType,
  SectionBehavior,
  TextPosition, VerticalPositionBottom,
  VerticalPositionTop, YoukuConfig, YoutubeConfig,
} from '../../global/_types';
import { Splide, Options } from '@splidejs/splide';
import { AutoScroll } from '@splidejs/splide-extension-auto-scroll';
import { Video } from '@splidejs/splide-extension-video';
import GLightbox from 'glightbox';
import { getSectionBehavior, renderBlockMargin } from '../../global/_utils';
import { adjustYoukuDOM, showConsent } from '../../global/controller/_lightbox';
import { videoIdSelector } from '../../global/_constants';

@Component({
  tag: 'cms-gallery',
  styleUrl: 'cms-gallery.scss',
  shadow: true,
})
export class CmsGallery {
  @Element() host: HTMLElement;

  @Prop() config: string;
  @Prop() debug: boolean = false;
  @Prop() behavior: string = '{}';

  private static defaultValues = {
    lightbox: true,
    textPosition: 'bottom'
  }

  private logger: Logger;

  /* Config */
  private options: CmsGalleryConfig;
  private splideOptions: Options;
  private thumbnailsOptions: Options;

  private lightbox: GLightbox;

  private readonly videoParams = 'autoplay=0&rel=0';
  private hasVideo: boolean = false;

  /* SectionBehavior */
  private sectionBehavior: SectionBehavior;

  // Lifecycle
  componentWillLoad() {
    this.logger = new Logger(this.debug, 'CmsGallery');

    this.options = { ...CmsGallery.defaultValues, ...JSON.parse(this.config)} as CmsGalleryConfig;

    // Set host for items
    this.options.items = this.options.items.map(item => {
        item.alt = item.alt || item.headline || '';
        item.title = item.headline ?
          (item.headlineStyle && item.headlineStyle != "" ? '<span class=\"' + item.headlineStyle + '\">' + item.headline + '</span>' : item.headline) :
          '';
        item.description = item.text || '';
        item.isVideo = item.type === 'video' || item.source === 'youtube' || item.source === 'youku';
        if (item.type !== 'video' || (item.source !=='youtube' && item.source !=='youku')) {
          item.thumbnail = item.thumbnail || item.href;
          return item;
        }
        item.thumbnail = item.thumbnail || item.preview;
        if (item.source ==='youtube') {
          item.id = item.href;
          item.href = this.options.youtube.playerUrl.replace(videoIdSelector, item.id);
          item.preview = item.preview || this.options.youtube.previewUrl.replace(videoIdSelector, item.id);
        }
        else if (item.source ==='youku') {
          // Create inline html for youku iframe because splide, glightbox and plyr do not support youku
          item.type = 'inline';
          item.id = item.href;
          const href = this.options.youku.playerUrl.replace(videoIdSelector, item.id)
            + (this.options.youku.playerUrl.includes('?') ? '&' : '?') + this.videoParams
            + (this.options.youku.clientId ? '&client_id=' + this.options.youku.clientId : '');
          item.href = null;
          item.preview = item.preview || this.options.youku.previewUrl.replace(videoIdSelector, item.id);
          item.content = '<iframe class="youku" src="' + href + '" frameborder="0" allowfullscreen="true"></iframe>';
        }
        return item;
    });

    // Video
    this.hasVideo = this.options.items.filter(item => item.isVideo).length > 0;

    // Lightbox
    if (this.options.lightbox) {
      this.lightbox = GLightbox({
        elements: this.options.items,
        autoplayVideos: false,
        loop: true,
        plyr: {
          css: `${stencilEnv.assetPath}plyr/plyr.css`,
          js: `${stencilEnv.assetPath}plyr/plyr.js`,
          config: {
            iconUrl: `${stencilEnv.assetPath}plyr/plyr.svg`,
            blankVideo: `${stencilEnv.assetPath}plyr/blank.mp4`,
            youtube: {
              noCookie: true,
              rel: 0,
              autoplay: 0
            }
          }
        }
      });
    }

    // Splide
    this.splideOptions = {
      ...{
        type: 'loop',
        perPage: 3,
        autoHeight: true,
        trimSpace: true,
        slideFocus: true,
        perMove: 1,
        width: '1200px',
        gap: '0.5rem',
        easing: 'linear',
        pagination: false,
        drag: true,
        keyboard: true,
        breakpoints: {
          768: {
            perPage: 1,
          },
          1068: {
            perPage: 2,
          },
        },
        video: {
          loop: false,
          mute: false,
          autoplay: false,
          disableOverlayUI: true,
          host: this.options.youtube?.host,
        },
        playerOptions: {
          youtube: {
            autoplay: false,
            mute: false,
            controls: 0
          },
          htmlVideo: {
            autoplay: false,
            mute: false,
            controls: 0
          },
        },
      },
      ...this.options.splideConfig,
    };

    this.thumbnailsOptions = {
      fixedWidth: 100,
      fixedHeight: 60,
      gap: 10,
      rewind: true,
      pagination: false,
      isNavigation: true,
      breakpoints: {
        600: {
          fixedWidth: 60,
          fixedHeight: 44,
        },
      },
    };

    // Dependant options
    // mouseWheel not allowed if autoScroll is active
    if (this.splideOptions.autoScroll) {
      this.splideOptions.wheel = false;
    }
    // arrows not allowed if thumbnails are active
    if (this.options.thumbnails) {
      this.splideOptions.arrows = false;
    }
    // restrictions if amount of items equals items per page
    if (this.options.items.length === this.splideOptions.perPage) {
      this.splideOptions.type = 'slide';
    }
    // activate pagination
    if (this.options.pagination === 'dot' || this.options.pagination === 'number') {
      this.splideOptions.pagination = true;
      // pagination with provided option perPage triggers slider to move pages not slides
      // -> to trigger slide pagination instead of page set:
      // this.splideOptions.focus = 0
      if (this.options.paging != null && !this.options.paging) {
        this.splideOptions.focus = 0;
      }
    }
    // banner styles: full width with noPadding and 100% image height
    if (this.options.banner) {
      this.splideOptions.width = 'auto';
      this.splideOptions.height = '500px';
      this.splideOptions.cover = true;
      this.splideOptions.perPage = 1;
      this.splideOptions.breakpoints = {
        768: {
          perPage: 1,
          height: '250px'
        },
        1068: {
          perPage: 1,
        }
      };
    }

    // Section-Behavior
    this.sectionBehavior = getSectionBehavior(this.behavior);
  }

  componentDidLoad() {
    const gallery = this.host.shadowRoot.querySelector('.splide') as HTMLElement;
    const splide = new Splide(gallery, this.splideOptions);

    // Assign events
    if (this.options.lightbox) {
      // Open lightbox
      splide.on('click', component => {
        const idx = component.index < 0 ? component.slideIndex : component.index % splide.length;
        // Force YouTube consent for current slide
        showConsent(this.lightbox, idx);
        // Open GLightbox
        this.lightbox.openAt(idx);
      });
      // Force YouTube consent
      showConsent(this.lightbox);
      // Adjust youku iframe (splide, glightbox and plyr do not support youku)
      adjustYoukuDOM(this.lightbox);
    } else if (this.options.banner && this.hasVideo) {
      splide.on('click', component => {
        if (gallery.classList.contains('is-playing')) {
          splide.Components.Video.pause();
          if (this.splideOptions.autoplay) {
            splide.Components.Autoplay.play();
          }
          return;
        }
        if (this.splideOptions.autoplay) {
          splide.Components.Autoplay.pause();
        }
        const idx = component.index < 0 ? component.slideIndex : component.index % splide.length;
        splide.Components.Video.play(idx);
      });
    }
    if (this.splideOptions.type === "loop") {
      // Loop flicker fix
      splide.on('overflow', isOverflow => {
        // Reset the carousel position
        splide.go( 0 );
        splide.options = {
          clones: isOverflow ? undefined : 0 // Toggle clones
        };
      });
    }
    if (this.options.pagination === 'number') {
      // Convert pagination dots to number
      splide.on( 'pagination:mounted', data => {
        // Add custom class to pagination UL element
        data.list.classList.add( 'splide__pagination--custom' );
        // `items` contains all dot items
        data.items.forEach(item => {
          item.button.textContent = String(item.page + 1);
        } );
      } );
    }

    if (this.options.progressBar) {
      const liList = Array.from(splide.root.querySelectorAll( '.splide__progress__bar__item' ));
      const parts = liList.map(li => li.children[0] as HTMLElement);
      let activePart = parts[0];
      splide.on('autoplay:playing', rate => {
        activePart.style.width = (rate * 100) + '%';
      });
      splide.on('mounted move', () => {
        parts.forEach((part, index) => {
          part.style.width = index < splide.index ? '100%' : '0';
        });
        activePart = parts[splide.index];
        if (!this.splideOptions.autoplay) {
          activePart.style.width = '100%';
        }
      });
    }

    // Restart autoplay after dragging
    if (this.splideOptions.autoplay) {
      splide.on('dragged', () => {
        splide.Components.Autoplay.play();
      });
    }

    // Activate autoplay and refresh after mounted
    splide.on('mounted', () => {
      this.logger.trace('Splide loaded');
      if (this.splideOptions.autoplay) {
        splide.Components.Autoplay.play();
      }
      // Add refresh EventListener (e.g. for modals) with standard addEventListener instead of @Listen function
      if (this.options.refreshEvent?.name) {
        const parent = this.options.refreshEvent?.elementId ?
          this.host.ownerDocument.querySelector(this.options.refreshEvent.elementId) : this.host.ownerDocument;
        parent.addEventListener(this.options.refreshEvent.name, () => {
          splide.event.emit('updated');
          splide.go(0);
          splide.refresh();
          if (this.splideOptions.autoplay) {
            splide.Components.Autoplay.play();
          }
        });
      }
    });

    // Mount gallery
    let mountOptions = { };
    if (this.splideOptions.autoScroll) {
      mountOptions = { ...mountOptions, AutoScroll };
    }
    if (this.options.banner && this.hasVideo) {
      mountOptions = { ...mountOptions, Video };
    }
    let thumbnails;
    if (this.options.thumbnails) {
      const thumbnailContainer = this.host.shadowRoot.querySelector('.thumbnails') as HTMLElement;
      thumbnails = new Splide(thumbnailContainer, this.thumbnailsOptions);
      splide.sync(thumbnails);
    }
    splide.mount(mountOptions);

    // Mount thumbnails
    if (this.options.thumbnails) {
      thumbnails.mount();
    }
  }

  // Render
  render() {
    // Section behavior
    const sectionClass = `${this.sectionBehavior.sectionClass} ${this.options.banner ? 'banner' : ''}
                          ${this.options.navOutside ? 'navOutside' : ''}
                          ${this.options.arrowPosition ? 'arrow-'+this.options.arrowPosition : ''}`;
    const trackClass = `splide__track ${this.options.overflow ? 'show-overflow' : ''}`;
    const slideClass = `splide__slide text-${this.options.textPosition}`;

    return (
      <Host class={this.sectionBehavior.hostClass}>
        <section style={this.sectionBehavior.sectionStyle} class={sectionClass}>
          { renderBlockMargin(this.sectionBehavior, VerticalPositionTop) }
          <div class="section-inner">
            {
              this.options.headline &&
              <div class='section-headline-wrapper'>
                <h3 class='headline'>{this.options.headline}</h3>
              </div>
            }
            <div class="splide">
              <div class={trackClass}>
                <ul class="splide__list">
                  {this.options.items.map(item => {
                    let attributes: Object = {};
                    if (this.options.banner && item.isVideo) {
                      if (item.source === 'youtube') {
                        attributes = { 'data-splide-youtube': item.href };
                      }
                      else if (item.source === 'youku') {
                        // ToDo: prepared for youku but not functional yet (splide, glightbox and plyr do not support youku)
                        attributes = { 'data-splide-youku': item.id };
                      } else {
                        attributes = { 'data-splide-html-video': item.href };
                      }
                    }
                    return (
                      <li class={`${slideClass} ${item.isVideo ? 'video' : ''}`} {...attributes}>
                        {this.getElement(item)}
                      </li>
                    );
                  })}
                </ul>
              </div>
              {
                this.options.progressBar &&
                <div class='splide__progress'>
                  <div class='splide__custom__progress__bar'>
                    <ul class='splide__progress__bar__list'>
                      {this.options.items.map(() => (
                        <li class='splide__progress__bar__item'><div /></li>
                      ))}
                    </ul>
                  </div>
                </div>
              }
            </div>
            {
              this.options.thumbnails &&
              <section class='splide thumbnails'>
                <div class='splide__track'>
                  <ul class='splide__list'>
                    {this.options.items.map(item => (
                      <li class='splide__slide'>
                        <img src={item.thumbnail} alt={item.alt} />
                      </li>
                    ))}
                  </ul>
                </div>
              </section>
            }
          </div>
          { renderBlockMargin(this.sectionBehavior, VerticalPositionBottom) }
        </section>
      </Host>
    );
  }

  private getElement(item: CmsGalleryItem) {
    const containerClass = `splide__slide__container ${item.source}
          ${this.lightbox || this.options.overlay || (item.link && item.link.link) ? 'pointer' : ''}`;
    const options = this.options;
    const debug = this.debug;

    function getDom() {
      return [
        <div class={containerClass} style={{ aspectRatio: options.aspectRatio ? options.aspectRatio.toString() : null }}>
          {
            options.overlay ? <div class='overlay' style={{ ...options.overlay }} /> : ''
          }
          <img
            class={`${ options.aspectRatio && options.aspectRatio > 1 ? 'landscape' : ''}`}
            src={item.isVideo ? item.preview : item.href} alt={item.alt}
          />
        </div>,
        <div class={`splide__slide__content`}>
          {
            item.headline
            ? <p innerHTML={
                item.headlineStyle && item.headlineStyle != '' ?
                `<span class="${item.headlineStyle}">${item.headline}</span>` :
                item.headline
              } />
            : ''
          }
          {
            item.text
            ? <div
                class={`${options.hideDescriptionInOverview ? 'splide__slide__content__hidden' : ''}`}
                innerHTML={item.text}
              />
            : ''
          }
          {
            item.link && item.type !== 'video'
            ? <div class="splide__slide__button">
                <cms-link-or-button debug={debug} config={item.link} />
              </div>
            : ''
          }
        </div>,
      ];
    }

    if (!this.lightbox && !this.options.overlay && item.link && item.link.link) {
      return (
        <cms-link-element debug={this.debug} config={item.link}>
          {getDom.call(this)}
        </cms-link-element>
      );
    }
    return getDom.call(this);
  }
}

interface CmsGalleryConfig {
  headline: string;
  overflow?: boolean;
  banner?: boolean;
  thumbnails?: boolean;
  items: CmsGalleryItem[];
  splideConfig: Options;
  youtube?: YoutubeConfig;
  youku?: YoukuConfig;
  lightbox?: boolean;
  progressBar?: boolean;
  pagination?: PaginationType;
  paging?: boolean; // triggers page (default) or slide pagination
  textPosition?: TextPosition;
  overlay?: CmsGalleryOverlay;
  hideDescriptionInOverview?: boolean;
  navOutside?: boolean;
  arrowPosition?: string;
  refreshEvent?: CustomEvent;
  aspectRatio?: number;
}

interface CmsGalleryItem {
  headline?: string;
  headlineStyle?: string; //hl-bold, caption, light-caption, dark-caption, banner, left, right, center
  text?: string;
  title?: string;
  alt?: string;
  description?: string;
  content?: string;
  source?: string;
  type: string;
  href?: string;
  thumbnail?: string;
  preview?: string;
  width?: string;
  id?: string;
  link?: CmsLinkConfig;
  isVideo: boolean;
}

interface CmsGalleryOverlay {
  backgroundColor: string;
  opacity: string;
}

interface CustomEvent {
  name: string;
  elementId?: string;
}
